import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// ── Types ──────────────────────────────────────────────────────────────────

interface BalanceGroup {
  balance_in_cents: number;
}

interface FarrData {
  usable: boolean;
  balance: number;
  days_remaining: number | null;
  term_end_date: string | null;
}

interface BalanceData {
  content: number;
  available: number;
  pending: number;
  regular_balance?: number;
  combined_available?: number;
  groups: {
    Editor: BalanceGroup;
    'Editor Flex': BalanceGroup;
    Strategy: BalanceGroup;
    Distribution: BalanceGroup;
  };
  farr?: FarrData;
}

// ── Utils ──────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  const dollars = Math.round(cents) / 100;
  const abs = Math.abs(dollars);
  const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return dollars < 0 ? `-$${formatted}` : `$${formatted}`;
}

// ── Styled components ──────────────────────────────────────────────────────

const Container = styled.div`
  min-width: 280px;
  max-width: 380px;
`;

const SectionTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
`;

const ItalicRow = styled(Row)`
  font-style: italic;
  padding-left: 12px;
  color: #555;
`;

const BoldRow = styled(Row)`
  font-weight: 700;
`;

const Tooltip = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ccc;
  color: white;
  font-size: 10px;
  text-align: center;
  line-height: 14px;
  cursor: help;
`;

const WarningText = styled.span`
  color: #e67700;
  font-weight: bold;
`;

const SmallText = styled.span`
  font-size: 11px;
  color: #666;
`;

// ── Component ──────────────────────────────────────────────────────────────

interface BankBalanceProps {
  publicationId: string;
}

function BankBalance({ publicationId }: BankBalanceProps) {
  const [data, setData] = useState<BalanceData | null>(null);

  useEffect(() => {
    fetch(`/api/v1/brand_profiles/${publicationId}/balance`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(r => r.json())
      .then(setData)
      .catch(() => {}); // silently fail — widget simply won't render
  }, [publicationId]);

  if (!data) return null;

  const hasFarr = Boolean(data.farr?.usable);
  const isExpiringWarning = hasFarr && data.farr!.days_remaining !== null && data.farr!.days_remaining! <= 30;

  const contentBalance = formatCents(data.content);
  const availableBalance = formatCents(data.available);
  const pendingPayments = formatCents(data.pending);
  const regularBalance = data.regular_balance !== undefined ? formatCents(data.regular_balance) : availableBalance;
  const combinedAvailable = data.combined_available !== undefined ? formatCents(data.combined_available) : availableBalance;
  const editorBalance = formatCents(data.groups.Editor.balance_in_cents);
  const editorFlexBalance = formatCents(data.groups['Editor Flex'].balance_in_cents);
  const strategyBalance = formatCents(data.groups.Strategy.balance_in_cents);
  const distributionBalance = formatCents(data.groups.Distribution.balance_in_cents);

  return (
    <Container>
      <SectionTitle>
        Bank Balance
        <Tooltip title="Content: funds for freelancers. Editor: fixed monthly ME fees. Editor Flex: ME overages. Strategy: deliverables. Distribution: monthly.">?</Tooltip>
      </SectionTitle>

      {hasFarr && (
        <>
          <Row>
            <span>
              {isExpiringWarning ? (
                <WarningText>Fixed ARR Balance ⚠️</WarningText>
              ) : (
                'Fixed ARR Balance'
              )}
            </span>
            <span>{formatCents(data.farr!.balance)}</span>
          </Row>
          <ItalicRow>
            <SmallText>Expires: {data.farr!.term_end_date}</SmallText>
            <SmallText>{data.farr!.days_remaining} days remaining</SmallText>
          </ItalicRow>
        </>
      )}

      <Row>
        <span>{hasFarr ? 'Regular Content' : 'Content'}</span>
        <span>{hasFarr ? regularBalance : contentBalance}</span>
      </Row>
      <ItalicRow>
        <span>
          Pending Payments
          <Tooltip title="Total upcoming payments on stories still in progress."> ?</Tooltip>
        </span>
        <span>-{pendingPayments}</span>
      </ItalicRow>
      <BoldRow>
        <span>
          {hasFarr ? 'Total Available' : 'Available Balance'}
          <Tooltip title="Total balance available to spend on content."> ?</Tooltip>
        </span>
        <span>{hasFarr ? combinedAvailable : availableBalance}</span>
      </BoldRow>

      <Row><span>Editor</span><span>{editorBalance}</span></Row>
      <Row>
        <span>
          Editor Flex
          <Tooltip title="Pays for additional ME hours and Contently creative managers."> ?</Tooltip>
        </span>
        <span>{editorFlexBalance}</span>
      </Row>
      <Row><span>Strategy</span><span>{strategyBalance}</span></Row>
      <Row><span>Distribution</span><span>{distributionBalance}</span></Row>
    </Container>
  );
}

export default BankBalance;
